import { Link } from 'react-router-dom';
import { Newspaper, Mic, Mail, Calendar, ExternalLink, User, Quote, Globe, AlertTriangle } from 'lucide-react';

const PressPage = () => {
  // Placeholder for future media coverage about SAFE
  const mediaCoverage = [
    {
      title: 'Coming Soon',
      outlet: 'Media Coverage',
      date: '',
      summary: 'Press coverage and media mentions will be featured here as SAFE launches publicly.',
      link: null,
      placeholder: true
    }
  ];

  // Topical news - related industry coverage
  const topicalNews = [
    {
      title: 'BC online safety advocates warn AI is making teen sextortion harder to spot',
      outlet: 'CTV News',
      date: 'March 2025',
      summary: 'Canadian advocates warn that AI-generated content is making it increasingly difficult to identify and prevent sextortion targeting teenagers.',
      link: 'https://www.ctvnews.ca/canada/article/bc-online-safety-advocates-warn-ai-is-making-teen-sextortion-harder-to-spot/',
      region: 'Canada'
    },
    {
      title: 'On Safer Internet Day, FBI Warns About the Dangers of Sextortion Schemes Against Minors',
      outlet: 'FBI',
      date: 'February 2025',
      summary: 'The FBI Kansas City field office issues warning about the growing threat of sextortion schemes targeting minors online.',
      link: 'https://www.fbi.gov/contact-us/field-offices/kansascity/news/on-safer-internet-day-fbi-warns-about-the-dangers-of-sextortion-schemes-against-minors',
      region: 'United States'
    },
    {
      title: 'Sextortion: A Growing Threat to Children and Teens',
      outlet: 'ICE / Homeland Security',
      date: 'Ongoing',
      summary: "U.S. Immigration and Customs Enforcement highlights sextortion as a rapidly growing form of online exploitation targeting young people.",
      link: 'https://www.ice.gov/features/sextortion',
      region: 'United States'
    },
    {
      title: 'Bangladeshi National Returned to United States in International Sextortion Case',
      outlet: 'U.S. Department of Justice',
      date: 'March 2025',
      summary: 'DOJ announces extradition in major international sextortion case, highlighting the global nature of online child exploitation.',
      link: 'https://www.justice.gov/opa/pr/bangladeshi-national-returned-united-states-international-sextortion-case',
      region: 'International'
    },
    {
      title: 'Online fraud and scams targeting children and young people',
      outlet: 'BBC News',
      date: 'March 2025',
      summary: 'BBC reports on the rising tide of online fraud and scams specifically targeting children and young people.',
      link: 'https://www.bbc.com/news/articles/czrpedexleno',
      region: 'United Kingdom'
    }
  ];

  return (
    <div data-testid="press-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-4">Resources</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6">
            Press & Media
          </h1>
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
            Media resources, press coverage, and expert commentary on AI-mediated child safeguarding.
          </p>
        </div>
      </section>

      {/* Media Contact / Subject Matter Expert */}
      <section className="section-padding bg-cyan-700 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Mic className="w-6 h-6 text-cyan-200" />
                <p className="text-xs uppercase tracking-widest text-cyan-200 font-semibold">Subject Matter Expert</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                Expert Commentary Available
              </h2>
              <p className="text-lg text-cyan-100 leading-relaxed mb-6">
                SAFE Founder & CEO Nyree Ayne is available for media interviews on topics including online child safety, AI governance, platform accountability, and digital safeguarding policy.
              </p>
              <div className="flex items-center gap-4">
                <a 
                  href="mailto:governance@safestandard.ai"
                  className="bg-white text-cyan-700 px-6 py-3 font-semibold uppercase tracking-wider hover:bg-cyan-50 transition-colors flex items-center gap-2"
                >
                  <Mail className="w-5 h-5" />
                  Media Enquiries
                </a>
              </div>
            </div>
            <div className="bg-white/10 border border-white/20 p-8">
              <div className="flex items-start gap-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/tj1b21sg_Nyree.jpeg"
                  alt="Nyree Ayne"
                  className="w-24 h-24 object-cover"
                />
                <div>
                  <h3 className="text-xl font-semibold text-white">Nyree Ayne</h3>
                  <p className="text-cyan-200 mb-3">Founder & CEO, SAFE</p>
                  <p className="text-sm text-cyan-100 leading-relaxed">
                    Expert in AI governance, child safeguarding, and digital safety policy. Board Member, World Orphanage Foundation.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-xs uppercase tracking-widest text-cyan-200 mb-3">Areas of Expertise</p>
                <div className="flex flex-wrap gap-2">
                  {['AI Governance', 'Child Safety', 'Platform Accountability', 'Digital Policy', 'Safeguarding Standards'].map((topic) => (
                    <span key={topic} className="bg-white/10 px-3 py-1 text-sm text-white">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topical News - Industry Coverage */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-amber-600" />
            <div>
              <p className="caption text-amber-600">Industry News</p>
              <h2 className="heading-2">The Global Conversation</h2>
            </div>
          </div>
          <p className="text-slate-600 mb-8 max-w-3xl">
            Governments, law enforcement, and media worldwide are recognizing the urgent need for better safeguarding. Here's what's being reported globally.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {topicalNews.map((item, index) => (
              <a 
                key={index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-slate-200 p-6 hover:border-amber-400 hover:shadow-md transition-all group"
                data-testid={`topical-news-${index}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-amber-600">{item.outlet}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-sm text-slate-500">{item.date}</span>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1">{item.region}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-amber-700 transition-colors">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4">{item.summary}</p>
                <div className="flex items-center gap-1 text-cyan-700 text-sm font-medium">
                  <span>Read article</span>
                  <ExternalLink className="w-4 h-4" />
                </div>
              </a>
            ))}
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-amber-900 mb-2">The Problem is Global — So is the Solution</p>
                <p className="text-amber-800 text-sm">
                  These stories highlight why independent governance standards are urgently needed. SAFE is building the infrastructure to address this crisis at scale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Coverage - About SAFE */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex items-center gap-3 mb-8">
            <Newspaper className="w-6 h-6 text-slate-400" />
            <div>
              <p className="caption text-cyan-700">In The News</p>
              <h2 className="heading-2">SAFE Media Coverage</h2>
            </div>
          </div>

          <div className="space-y-6">
            {mediaCoverage.map((item, index) => (
              <div 
                key={index} 
                className={`border border-slate-200 p-6 ${item.placeholder ? 'bg-slate-50' : 'bg-white hover:border-slate-400 transition-colors'}`}
              >
                {item.placeholder ? (
                  <div className="text-center py-8">
                    <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-slate-400">{item.title}</p>
                    <p className="text-slate-400 mt-2">{item.summary}</p>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold text-cyan-700">{item.outlet}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-sm text-slate-500">{item.date}</span>
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.summary}</p>
                    </div>
                    {item.link && (
                      <a 
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-cyan-700 hover:text-cyan-900 transition-colors flex-shrink-0"
                      >
                        <span className="text-sm font-medium">Read</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Press Kit */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Press Kit</p>
          <h2 className="heading-2 mb-8">Resources for Journalists</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 p-6">
              <Quote className="w-8 h-8 text-cyan-700 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Key Messages</h3>
              <p className="text-slate-600 text-sm mb-4">
                Core messaging and talking points about SAFE governance architecture.
              </p>
              <p className="text-xs text-slate-400">Available upon request</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <User className="w-8 h-8 text-cyan-700 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Founder Bio</h3>
              <p className="text-slate-600 text-sm mb-4">
                Official biography and high-resolution photos of Nyree Ayne.
              </p>
              <p className="text-xs text-slate-400">Available upon request</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <Calendar className="w-8 h-8 text-cyan-700 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Fact Sheet</h3>
              <p className="text-slate-600 text-sm mb-4">
                Key statistics, milestones, and background information.
              </p>
              <p className="text-xs text-slate-400">Available upon request</p>
            </div>
          </div>

          <div className="mt-8">
            <Link 
              to="/brand"
              className="inline-flex items-center gap-2 text-cyan-700 font-semibold hover:text-cyan-900 transition-colors"
            >
              View Brand Guidelines & Logo Assets →
            </Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight mb-6">
                Media Contact
              </h2>
              <p className="text-slate-300 mb-6">
                For press enquiries, interview requests, or media resources, please contact our communications team.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-cyan-400" />
                  <a href="mailto:governance@safestandard.ai" className="text-cyan-400 hover:text-cyan-300">
                    governance@safestandard.ai
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <a href="https://linkedin.com/company/safestandard/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                    linkedin.com/company/safestandard
                  </a>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-6">
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">Response Time</p>
              <p className="text-slate-300">
                We aim to respond to all media enquiries within 24 hours. For urgent requests, please indicate "URGENT" in your subject line.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressPage;
